/**
 * Achievement catalog — pedagogically-grounded writing milestones.
 *
 * Each achievement answers: "what did this student just do that a writer does?"
 *
 * NOT in this catalog (intentionally removed): "first period," "first comma,"
 * "first exclamation point." Using punctuation isn't a writing achievement —
 * it's using a keyboard. We celebrate moves that make a kid feel like a writer.
 *
 * Categories:
 *  - composition  : built a real piece (sentences → paragraphs → structure)
 *  - voice        : sounded like a writer (opinion, perspective, audience)
 *  - showDontTell : chose specific over generic (senses, names, numbers)
 *  - storyCraft   : narrative moves (setting, character, dialogue, resolution)
 *  - wordCraft    : deliberate word choice (vivid verbs, feeling vocabulary)
 *  - connections  : built logical structure (transitions, cause-effect)
 *  - discoveries  : pedagogically loaded craft moments (curiosity, simile)
 *
 * Tiers:
 *  - bronze : early wins, easy to hit, encourage starting
 *  - silver : real writing growth, requires intent
 *  - gold   : sophisticated craft, makes a kid say "I did that?"
 */

import { TextFeatures } from './analyzer';

export type AchievementCategory =
  | 'composition'
  | 'voice'
  | 'showDontTell'
  | 'storyCraft'
  | 'wordCraft'
  | 'connections'
  | 'discoveries';

export type AchievementTier = 'bronze' | 'silver' | 'gold';

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: AchievementCategory;
  tier: AchievementTier;
  isSecret?: boolean;
  condition: (f: TextFeatures) => boolean;
  progress?: (f: TextFeatures) => { current: number; target: number };
}

export const ACHIEVEMENT_CATALOG: Achievement[] = [
  // ─────────────────────────── COMPOSITION ───────────────────────────
  {
    id: 'first-words',
    name: 'First Words',
    description: 'You wrote your first words. Every writer starts here.',
    icon: '✨', category: 'composition', tier: 'bronze',
    condition: (f) => f.wordCount >= 1,
    progress: (f) => ({ current: Math.min(f.wordCount, 1), target: 1 }),
  },
  {
    id: 'a-real-sentence',
    name: 'A Real Sentence',
    description: 'You wrote a sentence that stands on its own.',
    icon: '🧱', category: 'composition', tier: 'bronze',
    condition: (f) => f.sentenceCount >= 1 && f.longestSentenceWords >= 4,
  },
  {
    id: 'idea-developer',
    name: 'Idea Developer',
    description: 'You stayed on the same topic for three sentences in a row.',
    icon: '🌱', category: 'composition', tier: 'silver',
    condition: (f) => f.longestParagraphSentences >= 3,
  },
  {
    id: 'paragraph-pro',
    name: 'Paragraph Pro',
    description: 'You wrote a full paragraph — four sentences working together.',
    icon: '📃', category: 'composition', tier: 'silver',
    condition: (f) => f.longestParagraphSentences >= 4,
  },
  {
    id: 'three-paragraph-piece',
    name: 'Three-Paragraph Piece',
    description: 'Intro, body, conclusion. You wrote a real essay.',
    icon: '📚', category: 'composition', tier: 'gold',
    condition: (f) => f.paragraphCount >= 3 && f.wordCount >= 100,
  },
  {
    id: 'sustained-effort',
    name: 'Sustained Effort',
    description: 'You wrote 150 words without giving up. That\'s writer\'s stamina.',
    icon: '💪', category: 'composition', tier: 'silver',
    condition: (f) => f.wordCount >= 150,
    progress: (f) => ({ current: f.wordCount, target: 150 }),
  },
  {
    id: 'real-piece',
    name: 'A Real Piece',
    description: 'You wrote 300 words. That\'s a complete short essay.',
    icon: '📝', category: 'composition', tier: 'gold',
    condition: (f) => f.wordCount >= 300,
    progress: (f) => ({ current: f.wordCount, target: 300 }),
  },

  // ─────────────────────────── VOICE & STANCE ───────────────────────────
  {
    id: 'first-person-voice',
    name: 'Your Own Voice',
    description: 'You wrote about yourself using "I." Only you could have written this.',
    icon: '👤', category: 'voice', tier: 'bronze',
    condition: (f) => f.hasFirstPerson,
  },
  {
    id: 'claim-with-reason',
    name: 'Claim + Reason',
    description: 'You stated an opinion AND backed it up with "because." That\'s real reasoning.',
    icon: '🎯', category: 'voice', tier: 'gold',
    condition: (f) => f.hasClaimWithReason,
  },
  {
    id: 'considered-counterview',
    name: 'Open Mind',
    description: 'You considered another point of view. Sophisticated writers do this.',
    icon: '⚖️', category: 'voice', tier: 'gold',
    condition: (f) => f.hasCounterView,
  },
  {
    id: 'wrote-for-reader',
    name: 'Wrote for a Reader',
    description: 'You addressed your reader directly with "you." Audience awareness!',
    icon: '👀', category: 'voice', tier: 'silver',
    condition: (f) => f.hasSecondPerson,
  },

  // ─────────────────────────── SHOW, DON'T TELL ───────────────────────────
  {
    id: 'sensory-detail',
    name: 'I Can See It',
    description: 'You used a sensory word — what you saw, heard, smelled, felt, or tasted.',
    icon: '👁️', category: 'showDontTell', tier: 'silver',
    condition: (f) => f.sensoryWordCount >= 1,
  },
  {
    id: 'painted-a-picture',
    name: 'Painted a Picture',
    description: 'You used three sensory details. Your reader can really see this.',
    icon: '🎨', category: 'showDontTell', tier: 'gold',
    condition: (f) => f.sensoryWordCount >= 3,
    progress: (f) => ({ current: f.sensoryWordCount, target: 3 }),
  },
  {
    id: 'named-it',
    name: 'Named It',
    description: 'You used a specific name. Specificity is what makes writing real.',
    icon: '🏷️', category: 'showDontTell', tier: 'bronze',
    condition: (f) => f.properNounCount >= 1,
  },
  {
    id: 'got-specific',
    name: 'Got Specific',
    description: 'You used a number. Specific beats vague every time.',
    icon: '🔢', category: 'showDontTell', tier: 'bronze',
    condition: (f) => f.hasNumber,
  },
  {
    id: 'precise-measurement',
    name: 'Precise',
    description: 'You used an actual measurement. Now your reader knows exactly.',
    icon: '📏', category: 'showDontTell', tier: 'silver',
    condition: (f) => f.hasMeasurement,
  },

  // ─────────────────────────── STORY CRAFT ───────────────────────────
  {
    id: 'set-the-scene',
    name: 'Set the Scene',
    description: 'You established when and where. Your reader knows the setting.',
    icon: '🗺️', category: 'storyCraft', tier: 'silver',
    condition: (f) => f.hasSettingMarker,
  },
  {
    id: 'wrote-dialogue',
    name: 'Dialogue Writer',
    description: 'You wrote what someone said. Characters speaking bring stories to life.',
    icon: '💬', category: 'storyCraft', tier: 'gold',
    condition: (f) => f.hasDialogue,
  },
  {
    id: 'character-on-the-page',
    name: 'Character on the Page',
    description: 'You named a character AND told us what they did or said.',
    icon: '🧑', category: 'storyCraft', tier: 'silver',
    condition: (f) => f.properNounCount >= 1 && (f.hasDialogue || f.vividVerbCount >= 1),
  },

  // ─────────────────────────── WORD CRAFT ───────────────────────────
  {
    id: 'vivid-verb',
    name: 'Vivid Verb',
    description: 'You picked a strong verb instead of "was" or "got." Writers do this.',
    icon: '⚡', category: 'wordCraft', tier: 'silver',
    condition: (f) => f.vividVerbCount >= 1,
  },
  {
    id: 'verb-virtuoso',
    name: 'Verb Virtuoso',
    description: 'Three vivid verbs in one piece. Your writing has movement and energy.',
    icon: '🔥', category: 'wordCraft', tier: 'gold',
    condition: (f) => f.vividVerbCount >= 3,
    progress: (f) => ({ current: f.vividVerbCount, target: 3 }),
  },
  {
    id: 'feeling-word',
    name: 'Named a Feeling',
    description: 'You named a feeling beyond happy or sad. Emotional precision!',
    icon: '❤️', category: 'wordCraft', tier: 'silver',
    condition: (f) => f.feelingWordCount >= 1,
  },
  {
    id: 'sophisticated-vocab',
    name: 'Sophisticated Vocabulary',
    description: 'You used five long, deliberate words. Word craft level up.',
    icon: '🎓', category: 'wordCraft', tier: 'silver',
    condition: (f) => f.bigWordCount >= 5,
    progress: (f) => ({ current: f.bigWordCount, target: 5 }),
  },
  {
    id: 'unique-vocabulary',
    name: 'Word Collector',
    description: 'You used 50 different words. Variety keeps writing interesting.',
    icon: '🎒', category: 'wordCraft', tier: 'silver',
    condition: (f) => f.uniqueWordCount >= 50,
    progress: (f) => ({ current: f.uniqueWordCount, target: 50 }),
  },

  // ─────────────────────────── CONNECTIONS ───────────────────────────
  {
    id: 'first-connection',
    name: 'Connected an Idea',
    description: 'You used a transition word to link two ideas — first, next, then, finally…',
    icon: '🪢', category: 'connections', tier: 'bronze',
    condition: (f) => f.transitionWordCount >= 1,
  },
  {
    id: 'three-transitions',
    name: 'Logical Flow',
    description: 'You used three different transitions. Your writing has shape.',
    icon: '🧭', category: 'connections', tier: 'silver',
    condition: (f) => f.transitionWordCount >= 3,
    progress: (f) => ({ current: f.transitionWordCount, target: 3 }),
  },
  {
    id: 'cause-effect',
    name: 'Cause and Effect',
    description: 'You explained WHY using "because." Reasoning is a writer\'s tool.',
    icon: '🔗', category: 'connections', tier: 'silver',
    condition: (f) => f.hasCausalConnection,
  },
  {
    id: 'gave-an-example',
    name: 'Gave an Example',
    description: 'You used "for example" or "for instance." You showed your thinking.',
    icon: '🔍', category: 'connections', tier: 'silver',
    condition: (f) => f.hasExampleConnector,
  },
  {
    id: 'signal-word',
    name: 'On Plan',
    description: 'You used a signal word from your writing plan.',
    icon: '🚦', category: 'connections', tier: 'bronze',
    condition: (f) => f.signalWordsUsed.size >= 1,
  },

  // ─────────────────────────── DISCOVERIES (secret) ───────────────────────────
  {
    id: 'curious-mind',
    name: 'Curious Mind',
    description: 'You wrote "I wonder…" — that\'s how every important idea starts.',
    icon: '🤔', category: 'discoveries', tier: 'gold', isSecret: true,
    condition: (f) => f.hasWonder,
  },
  {
    id: 'simile-spotter',
    name: 'Simile',
    description: 'You compared two things with "like" or "as." That\'s figurative language.',
    icon: '🎭', category: 'discoveries', tier: 'gold', isSecret: true,
    condition: (f) => f.hasSimile,
  },
];
